import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/api/client";
import Modal from "@/components/ui/Modal";
import Button from "@/components/ui/Button";
import toast from "react-hot-toast";
import clsx from "clsx";

const EVENT_TYPES = [
  { key: "personal",  label: "Persönlich",  icon: "📝", color: "#8b5cf6" },
  { key: "quiz",      label: "Quiz",         icon: "📚", color: "#06b6d4" },
  { key: "task",      label: "Aufgabe",      icon: "✅", color: "#22c55e" },
  { key: "reminder",  label: "Erinnerung",   icon: "🔔", color: "#f59e0b" },
];

const TYPE_MAP = Object.fromEntries(EVENT_TYPES.map((t) => [t.key, t]));

const WEEKDAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
const MONTHS   = ["Januar","Februar","März","April","Mai","Juni","Juli","August","September","Oktober","November","Dezember"];

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year, month) {
  // 0 = Monday, 6 = Sunday (ISO)
  return (new Date(year, month, 1).getDay() + 6) % 7;
}

function isSameDay(d1, d2) {
  return d1.getFullYear() === d2.getFullYear() &&
         d1.getMonth() === d2.getMonth() &&
         d1.getDate() === d2.getDate();
}

/* ── Event dot ────────────────────────────────────────────────────────────── */
function EventDots({ events }) {
  return (
    <div className="flex gap-0.5 justify-center mt-0.5">
      {events.slice(0, 3).map((e, i) => (
        <div
          key={i}
          className="w-1 h-1 rounded-full"
          style={{ background: e.color || TYPE_MAP[e.type]?.color || "#8b5cf6" }}
        />
      ))}
    </div>
  );
}

/* ── Add event form ───────────────────────────────────────────────────────── */
function AddEventModal({ open, onClose, defaultDate, onCreate }) {
  const [title, setTitle]       = useState("");
  const [type, setType]         = useState("personal");
  const [startDate, setStart]   = useState(defaultDate || "");
  const [emoji, setEmoji]       = useState("📝");
  const [loading, setLoading]   = useState(false);

  const EMOJIS = ["📝","📚","✅","🔔","🎯","💡","⭐","🏆","📅","🎓","🔥","💪"];

  const handleSubmit = async () => {
    if (!title.trim() || !startDate) return toast.error("Titel und Datum eingeben!");
    setLoading(true);
    try {
      await onCreate({ title, type, startDate, emoji, color: TYPE_MAP[type]?.color || "#8b5cf6" });
      setTitle(""); setType("personal"); setEmoji("📝");
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Neuer Termin">
      <div className="space-y-4">
        {/* Emoji picker */}
        <div>
          <p className="text-xs text-gray-400 mb-2">Emoji</p>
          <div className="flex flex-wrap gap-2">
            {EMOJIS.map((e) => (
              <button
                key={e}
                onClick={() => setEmoji(e)}
                className={clsx("w-9 h-9 rounded-xl text-lg transition-all", emoji === e ? "bg-violet-600 scale-110" : "bg-white/5 hover:bg-white/10")}
              >
                {e}
              </button>
            ))}
          </div>
        </div>

        {/* Title */}
        <input
          type="text"
          placeholder="Was ist geplant?"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-violet-500"
        />

        {/* Type */}
        <div className="grid grid-cols-2 gap-2">
          {EVENT_TYPES.map((t) => (
            <button
              key={t.key}
              onClick={() => setType(t.key)}
              className={clsx(
                "flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs font-bold border transition-all",
                type === t.key
                  ? "border-violet-500 bg-violet-500/20 text-white"
                  : "border-white/10 bg-white/5 text-gray-400 hover:bg-white/10"
              )}
            >
              <span>{t.icon}</span><span>{t.label}</span>
            </button>
          ))}
        </div>

        {/* Date */}
        <div>
          <p className="text-xs text-gray-400 mb-1.5">Datum</p>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStart(e.target.value)}
            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-violet-500"
          />
        </div>

        <Button full loading={loading} onClick={handleSubmit}>
          ✅ Termin erstellen
        </Button>
      </div>
    </Modal>
  );
}

/* ── Day detail modal ─────────────────────────────────────────────────────── */
function DayModal({ date, events, open, onClose, onDelete, onToggle }) {
  if (!date) return null;
  const dayEvents = events.filter((e) => isSameDay(new Date(e.startDate), date));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={date.toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "long" })}
    >
      <div className="space-y-2">
        {dayEvents.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-6">Keine Termine an diesem Tag</p>
        ) : (
          dayEvents.map((e) => {
            const typeInfo = TYPE_MAP[e.type] || {};
            return (
              <div key={e.id} className={clsx("flex items-center gap-3 p-3 rounded-xl border", e.isCompleted ? "opacity-50 border-white/5" : "border-white/10 bg-white/5")}>
                <span className="text-xl">{e.emoji}</span>
                <div className="flex-1 min-w-0">
                  <p className={clsx("text-sm font-bold", e.isCompleted && "line-through text-gray-500")}>{e.title}</p>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold" style={{ background: `${typeInfo.color}20`, color: typeInfo.color }}>
                    {typeInfo.icon} {typeInfo.label}
                  </span>
                </div>
                <div className="flex gap-1.5">
                  <button
                    onClick={() => onToggle(e)}
                    className={clsx("px-2.5 py-1.5 rounded-lg text-[11px] font-bold transition-colors", e.isCompleted ? "bg-gray-500/20 text-gray-400" : "bg-green-500/20 text-green-400 hover:bg-green-500/30")}
                  >
                    {e.isCompleted ? "↩️" : "✓"}
                  </button>
                  <button
                    onClick={() => onDelete(e.id)}
                    className="px-2.5 py-1.5 rounded-lg text-[11px] font-bold bg-red-500/10 text-red-400 hover:bg-red-500/30 transition-colors"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </Modal>
  );
}

/* ── Main ─────────────────────────────────────────────────────────────────── */
export default function LernCalendar() {
  const qc = useQueryClient();
  const today = new Date();
  const [year,  setYear]  = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [addOpen,  setAddOpen]  = useState(false);
  const [dayOpen,  setDayOpen]  = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [defaultDate, setDefaultDate] = useState("");

  // Load events for current month + surrounding
  const { data } = useQuery({
    queryKey: ["calendar", year, month],
    queryFn: () => {
      const from = new Date(year, month - 1, 1).toISOString();
      const to   = new Date(year, month + 2, 0).toISOString();
      return api.get(`/calendar?from=${from}&to=${to}`).then((r) => r.data);
    },
    staleTime: 30_000,
  });

  const events = data?.events || [];

  const create = useMutation({
    mutationFn: (ev) => api.post("/calendar", ev),
    onSuccess: () => { toast.success("Termin erstellt!"); qc.invalidateQueries({ queryKey: ["calendar"] }); },
    onError: (err) => toast.error(err.response?.data?.error || "Fehler"),
  });

  const del = useMutation({
    mutationFn: (id) => api.delete(`/calendar/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["calendar"] }); },
    onError: (err) => toast.error(err.response?.data?.error || "Fehler"),
  });

  const toggle = useMutation({
    mutationFn: (ev) => api.patch(`/calendar/${ev.id}`, { isCompleted: !ev.isCompleted }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["calendar"] }),
    onError: (err) => toast.error(err.response?.data?.error || "Fehler"),
  });

  // Build calendar grid
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay    = getFirstDayOfMonth(year, month);
  const cells = useMemo(() => {
    const arr = [];
    for (let i = 0; i < firstDay; i++) arr.push(null);
    for (let d = 1; d <= daysInMonth; d++) arr.push(d);
    return arr;
  }, [year, month, daysInMonth, firstDay]);

  const prevMonth = () => { if (month === 0) { setMonth(11); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 11) { setMonth(0);  setYear(y => y + 1); } else setMonth(m => m + 1); };

  const getEventsForDay = (d) => {
    if (!d) return [];
    const date = new Date(year, month, d);
    return events.filter((e) => isSameDay(new Date(e.startDate), date));
  };

  const handleDayClick = (d) => {
    if (!d) return;
    const date = new Date(year, month, d);
    setSelectedDay(date);
    setDefaultDate(`${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`);
    setDayOpen(true);
  };

  // Upcoming events (next 7 days)
  const upcoming = events
    .filter((e) => !e.isCompleted && new Date(e.startDate) >= today)
    .sort((a, b) => new Date(a.startDate) - new Date(b.startDate))
    .slice(0, 5);

  return (
    <div className="space-y-5 animate-fade-in max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-black flex items-center gap-2">
            <span className="text-2xl">📅</span>
            <span>Lernplaner</span>
          </h1>
          <p className="text-xs text-gray-400 mt-0.5">Plane deine Lernzeiten</p>
        </div>
        <Button size="sm" onClick={() => { setDefaultDate(""); setAddOpen(true); }}>
          + Termin
        </Button>
      </div>

      {/* Upcoming strip */}
      {upcoming.length > 0 && (
        <div className="bg-violet-500/10 border border-violet-500/30 rounded-2xl p-4">
          <p className="text-xs font-bold text-violet-300 mb-2">📌 Nächste Termine</p>
          <div className="space-y-2">
            {upcoming.map((e) => {
              const typeInfo = TYPE_MAP[e.type] || {};
              const d = new Date(e.startDate);
              const isToday = isSameDay(d, today);
              return (
                <div key={e.id} className="flex items-center gap-3">
                  <div className="w-10 flex-shrink-0 text-center">
                    <p className="text-[10px] text-gray-500">{WEEKDAYS[(d.getDay() + 6) % 7]}</p>
                    <p className={clsx("text-sm font-black", isToday ? "text-violet-400" : "text-gray-300")}>{d.getDate()}</p>
                  </div>
                  <div className="w-px h-8 bg-white/10 flex-shrink-0" />
                  <span className="text-lg">{e.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-white truncate">{e.title}</p>
                    <span className="text-[10px] font-bold" style={{ color: typeInfo.color }}>{typeInfo.label}</span>
                  </div>
                  {isToday && <span className="text-[10px] bg-violet-600 text-white px-2 py-0.5 rounded-full font-bold">Heute</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Calendar grid */}
      <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
        {/* Month navigation */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <button onClick={prevMonth} className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-gray-300 transition-colors">‹</button>
          <p className="font-black text-white">{MONTHS[month]} {year}</p>
          <button onClick={nextMonth} className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-gray-300 transition-colors">›</button>
        </div>

        {/* Weekday labels */}
        <div className="grid grid-cols-7 border-b border-white/10">
          {WEEKDAYS.map((d) => (
            <div key={d} className="text-center py-2 text-[10px] font-bold text-gray-600 uppercase">{d}</div>
          ))}
        </div>

        {/* Day cells */}
        <div className="grid grid-cols-7">
          {cells.map((day, idx) => {
            if (!day) return <div key={idx} className="h-14" />;
            const date = new Date(year, month, day);
            const dayEvents = getEventsForDay(day);
            const isToday = isSameDay(date, today);
            const hasEvents = dayEvents.length > 0;

            return (
              <button
                key={idx}
                onClick={() => handleDayClick(day)}
                className={clsx(
                  "h-14 flex flex-col items-center justify-start pt-2 border-b border-r border-white/5 transition-all hover:bg-white/10",
                  isToday && "bg-violet-500/10"
                )}
              >
                <span className={clsx(
                  "w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold",
                  isToday ? "bg-violet-600 text-white" : "text-gray-400"
                )}>
                  {day}
                </span>
                {hasEvents && <EventDots events={dayEvents} />}
              </button>
            );
          })}
        </div>
      </div>

      {/* Modals */}
      <AddEventModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        defaultDate={defaultDate}
        onCreate={(ev) => create.mutateAsync(ev)}
      />

      <DayModal
        date={selectedDay}
        events={events}
        open={dayOpen}
        onClose={() => setDayOpen(false)}
        onDelete={(id) => del.mutate(id)}
        onToggle={(ev) => toggle.mutate(ev)}
      />
    </div>
  );
}
